/**
 * Second-window work order view (#3653) — React side of lib/workOrderWindow.
 *
 * useWorkOrderWindowSync runs once per page (WorkOrderWindowControl in the
 * header). In every window it tracks live followers; in the follower window it
 * also answers the list: navigates on `open`, reports its mutations as
 * `changed` so the list refetches without waiting for the socket, and reports
 * the work order its route shows as `viewing` so the list can highlight it
 * (#3744).
 *
 * useOpenWorkOrder is what list rows call instead of navigate().
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import {
  WORK_ORDER_WINDOW_ACK_TIMEOUT_MS,
  createId,
  isFollowingWindow,
  isSameScope,
  isWorkOrderWindow,
  isWorkOrderWindowSupported,
  postWorkOrderWindowMessage,
  setWindowFollowing,
  subscribeToWorkOrderWindow,
  type WorkOrderWindowScope,
} from '@/lib/workOrderWindow';
import { navigateAwayFromWorkOrderDetail, workOrderIdFromPath } from '@/lib/workOrderNavigation';
import { queryKeys } from './queryKeys';

// ─── Module state (one page = one store) ────────────────────────────────────

let scope: WorkOrderWindowScope | null = null;
const followers = new Set<string>();
const pendingAcks = new Map<string, (acked: boolean) => void>();
const listeners = new Set<() => void>();
let following = false;
/** What a follower last said it shows; cleared when that follower goes away. */
let viewed: { windowId: string; workOrderId: string | null } | null = null;
/** Follower side: the work order this window's route shows. */
let shownWorkOrderId: string | null = null;
const windowId = createId();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setViewed(next: typeof viewed): void {
  if (viewed?.windowId === next?.windowId && viewed?.workOrderId === next?.workOrderId) return;
  viewed = next;
  emit();
}

function setFollowers(update: () => void): void {
  const before = followers.size;
  update();
  if (viewed !== null && !followers.has(viewed.windowId)) {
    viewed = null;
    emit();
  } else if ((before > 0) !== (followers.size > 0)) {
    emit();
  }
}

function postViewing(current: WorkOrderWindowScope): void {
  postWorkOrderWindowMessage({
    ...current,
    type: 'viewing',
    windowId,
    workOrderId: shownWorkOrderId,
  });
}

function hasFollowerSnapshot(): boolean {
  return followers.size > 0;
}

function followingSnapshot(): boolean {
  return following;
}

function viewedSnapshot(): string | null {
  return viewed?.workOrderId ?? null;
}

/** Test seam: forget followers, pending acks and scope between tests. */
export function resetWorkOrderWindowState(): void {
  scope = null;
  followers.clear();
  for (const resolve of pendingAcks.values()) resolve(false);
  pendingAcks.clear();
  following = false;
  viewed = null;
  shownWorkOrderId = null;
  emit();
}

/**
 * Posts `open` and resolves true when a follower acks. A missing ack means the
 * followers we knew of are gone (closed without `bye`), so they are dropped.
 */
function handOff(workOrderId: string): Promise<boolean> {
  if (scope === null) return Promise.resolve(false);
  const requestId = createId();
  const current = scope;
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => {
      pendingAcks.delete(requestId);
      setFollowers(() => followers.clear());
      resolve(false);
    }, WORK_ORDER_WINDOW_ACK_TIMEOUT_MS);
    pendingAcks.set(requestId, (acked) => {
      clearTimeout(timer);
      pendingAcks.delete(requestId);
      resolve(acked);
    });
    postWorkOrderWindowMessage({ ...current, type: 'open', requestId, workOrderId });
  });
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useWorkOrderWindowSync(): void {
  const userId = useAuthStore((s) => s.user?.id);
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const isFollowing = useSyncExternalStore(subscribe, followingSnapshot, () => false);

  // Before the effect below, so its first hello already carries the route.
  useEffect(() => {
    shownWorkOrderId = workOrderIdFromPath(pathname);
    if (isFollowing && scope !== null) postViewing(scope);
  }, [pathname, isFollowing]);

  useEffect(() => {
    if (!isWorkOrderWindowSupported() || !userId || !tenantId) return;
    const mine: WorkOrderWindowScope = { userId, tenantId };
    scope = mine;
    following = isFollowingWindow();
    emit();

    const sayHello = () => {
      if (!following) return;
      postWorkOrderWindowMessage({ ...mine, type: 'hello', windowId });
      postViewing(mine);
    };
    const sayBye = () => {
      postWorkOrderWindowMessage({ ...mine, type: 'bye', windowId });
    };

    const unsubscribe = subscribeToWorkOrderWindow((message) => {
      if (!isSameScope(message, mine)) return;
      switch (message.type) {
        case 'probe':
          sayHello();
          return;
        case 'hello':
          setFollowers(() => followers.add(message.windowId));
          return;
        case 'bye':
          setFollowers(() => followers.delete(message.windowId));
          return;
        case 'ack':
          setFollowers(() => followers.add(message.windowId));
          pendingAcks.get(message.requestId)?.(true);
          return;
        case 'open':
          if (!following) return;
          // Pathname change: WorkOrderDetailPage's blocker still guards unsaved
          // edits. Detail → detail needs the flushSync path (#842).
          navigateAwayFromWorkOrderDetail(
            window.location.pathname,
            `/workorders/${message.workOrderId}`,
            (to, options) => { void navigate(to, options); },
          );
          postWorkOrderWindowMessage({ ...mine, type: 'ack', requestId: message.requestId, windowId });
          return;
        case 'changed':
          void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
          void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.stats() });
          return;
        case 'viewing':
          setFollowers(() => followers.add(message.windowId));
          setViewed({ windowId: message.windowId, workOrderId: message.workOrderId });
          return;
      }
    });

    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (!following) return;
      if (event.type === 'updated' && event.action.type === 'success') {
        postWorkOrderWindowMessage({ ...mine, type: 'changed' });
      }
    });

    const handlePageHide = () => {
      if (following) sayBye();
    };
    window.addEventListener('pagehide', handlePageHide);

    sayHello();
    postWorkOrderWindowMessage({ ...mine, type: 'probe' });

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      unsubscribeMutations();
      unsubscribe();
      if (following) sayBye();
      setFollowers(() => followers.clear());
      scope = null;
    };
  }, [userId, tenantId, navigate, queryClient]);
}

/** True while at least one follower window has answered for this user + tenant. */
export function useHasWorkOrderWindow(): boolean {
  return useSyncExternalStore(subscribe, hasFollowerSnapshot, () => false);
}

/**
 * The work order a live follower window shows, for highlighting it in lists
 * and the calendar; null without one or when it shows something else.
 */
export function useViewedWorkOrderId(): string | null {
  return useSyncExternalStore(subscribe, viewedSnapshot, () => null);
}

/** Follower-side toggle: `isWorkOrderWindow` is false in an ordinary window. */
export function useWorkOrderWindowFollowing() {
  const isFollowing = useSyncExternalStore(subscribe, followingSnapshot, () => false);
  const setFollowing = useCallback((next: boolean) => {
    setWindowFollowing(next);
    following = next;
    if (scope !== null) {
      postWorkOrderWindowMessage({ ...scope, type: next ? 'hello' : 'bye', windowId });
    }
    emit();
  }, []);
  return { isWorkOrderWindow: isWorkOrderWindow(), isFollowing, setFollowing } as const;
}

/**
 * Hands the work order to a live second window; runs `fallback` here when
 * there is none, this window is the follower, or no ack arrives in time.
 */
function openElsewhereOr(workOrderId: string, fallback: () => void): void {
  if (following || followers.size === 0) {
    fallback();
    return;
  }
  void handOff(workOrderId).then((acked) => {
    if (!acked) fallback();
  });
}

/**
 * For views that show a work order in place without navigating, such as the
 * calendar's dialog: the second window takes it when one is live.
 */
export function useHandOffWorkOrder() {
  return useCallback((workOrderId: string, fallback: () => void) => {
    openElsewhereOr(workOrderId, fallback);
  }, []);
}

type OpenEvent = Pick<MouseEvent, 'ctrlKey' | 'metaKey' | 'button'>;

/**
 * Opens a work order: in the follower window when one is live, otherwise here.
 * Ctrl/Cmd/middle click opens a new tab as a link would.
 */
export function useOpenWorkOrder() {
  const navigate = useNavigate();
  return useCallback(
    (workOrderId: string, event?: OpenEvent) => {
      const path = `/workorders/${workOrderId}`;
      if (event && (event.ctrlKey || event.metaKey || event.button === 1)) {
        window.open(path, '_blank', 'noopener');
        return;
      }
      openElsewhereOr(workOrderId, () => { void navigate(path); });
    },
    [navigate],
  );
}
